import { apiClient } from '../utils/api-client';
import type { ClimbScan, PhotoArea } from '../types/climb-scan';
import { parseJson } from './climb-scans';

/**
 * **The scan of a board's photo** (docs/SPRAY_WALL_SCAN_PLAN.md): the holds a
 * gym's own wall is described by. One per photo, read and edited from the board
 * panel; the same scan the scans page shows a wall as, at the board's address.
 */
class BoardHoldScansService {
  private path(gymSlug: string, boardId: string) {
    return `/api/gyms/${gymSlug}/boards/${boardId}/hold_scan`;
  }

  /** The current photo's scan, or null when it has none yet. */
  async get(gymSlug: string, boardId: string): Promise<ClimbScan | null> {
    const response = await apiClient.get(this.path(gymSlug, boardId));
    const json = await parseJson<{ data: ClimbScan | null }>(response);
    return json.data;
  }

  /** "AI magic": the current photo, sent to be read. */
  async start(gymSlug: string, boardId: string): Promise<ClimbScan> {
    const response = await apiClient.post(this.path(gymSlug, boardId), {});
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /** Stops a run that will not come back; the photo can then be scanned again. */
  async stop(gymSlug: string, boardId: string): Promise<ClimbScan> {
    const response = await apiClient.delete(this.path(gymSlug, boardId));
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  async update(
    gymSlug: string,
    boardId: string,
    attrs: { min_score?: number | null; excluded_holds?: number[] }
  ): Promise<ClimbScan> {
    const response = await apiClient.patch(this.path(gymSlug, boardId), { data: { attributes: attrs } });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /** A hold outlined by hand with the colour-wand, on the 0-1000 grid. */
  async addHold(gymSlug: string, boardId: string, outline: Array<[number, number]>): Promise<ClimbScan> {
    const response = await apiClient.post(`${this.path(gymSlug, boardId)}/holds`, {
      data: { attributes: { outline } },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * Two touching holds made one: the joined hold appended, the two excluded —
   * nothing deleted, so a problem on either keeps it.
   */
  async mergeHolds(gymSlug: string, boardId: string, places: [number, number]): Promise<ClimbScan> {
    const response = await apiClient.post(`${this.path(gymSlug, boardId)}/merge`, {
      data: { attributes: { places } },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /** Look again at an area of the photo for holds the scan missed. */
  async scanArea(gymSlug: string, boardId: string, area: PhotoArea): Promise<ClimbScan> {
    const response = await apiClient.post(`${this.path(gymSlug, boardId)}/scan_area`, {
      data: { attributes: { area } },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }
}

export const boardHoldScansService = new BoardHoldScansService();
